import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip
} from '@mui/material';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { withStyles } from 'tss-react/mui';
import { getMostRecentProfileForUser } from '../../api/profiles';
import { exportPassportReports } from '../../api/reports';
import { getLastApprovedVcm } from '../../api/user';
import { useSearchPassportReportsQuery } from '../../hooks/useReports';

const styles = (theme) => ({
  tableContainer: {
    overflowX: 'auto'
  },
  tableHead: {
    backgroundColor: '#f5f5f5'
  },
  tableCell: {
    fontSize: theme.typography.pxToRem(13),
    padding: theme.typography.pxToRem(8),
    borderBottom: '1px solid #e0e0e0'
  },
  clickableTableCell: {
    fontSize: theme.typography.pxToRem(13),
    padding: theme.typography.pxToRem(8),
    borderBottom: '1px solid #e0e0e0',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  nonSortableHeaderCell: {
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 600,
    color: '#666',
    padding: theme.typography.pxToRem(8),
    borderBottom: '1px solid #e0e0e0'
  },
  headerCell: {
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 600,
    color: '#666',
    padding: theme.typography.pxToRem(8),
    borderBottom: '1px solid #e0e0e0',
    cursor: 'pointer'
  },
  statusCell: {
    fontSize: theme.typography.pxToRem(13),
    padding: theme.typography.pxToRem(8),
    borderBottom: '1px solid #e0e0e0',
    textAlign: 'center'
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    display: 'inline-block',
    marginRight: 0
  },
  progressBar: {
    width: '100%',
    height: 6,
    backgroundColor: '#e0e0e0',
    borderRadius: 3,
    overflow: 'hidden',
    position: 'relative',
    display: 'flex'
  },
  progressFill: {
    height: '100%',
    transition: 'width 0.3s ease'
  },
  pointsContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.typography.pxToRem(4)
  },
  pointsText: {
    fontSize: theme.typography.pxToRem(11),
    fontWeight: 500,
    minWidth: theme.typography.pxToRem(40)
  },
  popper: {
    opacity: 1
  },
  tooltipContainer: {
    backgroundColor: theme.palette.common.white,
    borderRadius: '2px',
    boxShadow: '0 2px 10px 0 #00000028',
    padding: theme.typography.pxToRem(20),
    opacity: 1
  },
  tooltipText: {
    color: '#0c0606de',
    fontSize: theme.typography.pxToRem(12),
    lineHeight: 1.5
  },
  infoIcon: {
    color: '#1F1F1F66',
    marginLeft: theme.typography.pxToRem(4),
    height: theme.typography.pxToRem(16)
  }
});

const PassportTable = ({ classes, institute, filters, title, showExportButton, filterChips }) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [orderBy, setOrderBy] = React.useState('user_full_name');
  const [order, setOrder] = React.useState('asc');

  const navigate = useNavigate();

  // A new filter set has its own page count, so go back to the first page
  // rather than requesting a page that may no longer exist.
  React.useEffect(() => {
    setPage(0);
  }, [filters]);

  const { data: passportReports, isPending } = useSearchPassportReportsQuery({
    page: page,
    per_page: rowsPerPage,
    sort_by: orderBy,
    sort_direction: order,
    filters: filters
  });

  const totalReports = React.useMemo(
    () => passportReports?.meta?.total_count || 0,
    [passportReports]
  );
  const reports = React.useMemo(
    () => passportReports?.data || [],
    [passportReports]
  );

  const getPassportStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'complete':
        return '#b8e986';
      case 'in_progress':
        return '#f5a623';
      case 'incomplete':
        return '#fd4f64';
      default:
        return '#9e9e9e';
    }
  };

  const renderProgressBar = (current, pending, required) => {
    const currentNum = parseInt(current);
    const pendingNum = parseInt(pending);
    const requiredNum = parseInt(required);

    const currentPercent = Math.min((currentNum / requiredNum) * 100, 100);
    const pendingPercent = Math.min((pendingNum / requiredNum) * 100, 100);
    const remainingPercent = Math.max(100 - currentPercent - pendingPercent, 0);

    return (
      <div className={classes.pointsContainer}>
        <div className={classes.progressBar}>
          {/* Current section */}
          {currentPercent > 0 && (
            <>
              <div
                className={classes.progressFill}
                style={{
                  width: `${currentPercent}%`,
                  backgroundColor: '#44A047',
                  borderRadius: '3px 0 0 3px'
                }}
              />
              {(pendingPercent > 0 || remainingPercent > 0) && (
                <div style={{ width: '1.4px', minWidth: '1px', backgroundColor: '#fff' }} />
              )}
            </>
          )}

          {/* Pending section */}
          {pendingPercent > 0 && (
            <>
              <div
                className={classes.progressFill}
                style={{
                  width: `${pendingPercent}%`,
                  backgroundColor: '#4A90E2',
                  borderRadius: '0'
                }}
              />
              {remainingPercent > 0 && (
                <div style={{ width: '1.4px', minWidth: '1px', backgroundColor: '#fff' }} />
              )}
            </>
          )}

          {/* Remaining section */}
          {remainingPercent > 0 && (
            <div
              className={classes.progressFill}
              style={{
                width: `${remainingPercent}%`,
                backgroundColor: '#e0e0e0',
                borderRadius: '0 3px 3px 0'
              }}
            />
          )}
        </div>
        <Typography className={classes.pointsText}>
          <span style={{ color: '#44A047', fontWeight: 600 }}>
            {currentNum}
          </span>
          <span style={{ color: '#000000' }}>/</span>
          <span style={{ color: '#4A90E2', fontWeight: 600 }}>
            {pendingNum}
          </span>
          <span style={{ color: '#000000' }}>/</span>
          <span style={{ color: '#000000', fontWeight: 600 }}>
            {requiredNum}
          </span>
        </Typography>
      </div>
    );
  };

  const formatRequirementStatus = (value) => {
    if (value === null || value === undefined) {
      return '-';
    }

    return value ? 'Complete' : 'Incomplete';
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0); // Reset to first page when sorting
  };

  const handleExport = () => {
    const csvUrl = exportPassportReports({ filters, order, orderBy });
    window.open(csvUrl, '_blank');
  };

  const createSortHandler = (property) => (event) => {
    handleRequestSort(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOnVcmCellClicked = async (row) => {
    if (history) {
      try {
        const result = await getLastApprovedVcm(row.user_id);
        if (result) {
          navigate(`/vcm/${result.id}`);
        }
      } catch (error) {
        console.error('Error fetching VCM:', error);
      }
    }
  };

  const handleOnProfileCellClicked = async (row) => {
    if (history) {
      try {
        const status = row.profile_status.toLowerCase();
        const namedFilter =
          status === 'educator complete'
            ? 'open'
            : status === 'complete'
            ? 'resolved'
            : 'in_progress';
        const result = await getMostRecentProfileForUser(
          row.user_id,
          namedFilter
        );

        if (result && result[0]?.id) {
          navigate(`/profiling/profile/${result[0].id}`);
        }
      } catch (error) {
        console.error('Error fetching VCM:', error);
      }
    }
  };

  const handleOnUserCellClicked = (row) => {
    if (history) {
      navigate(`/users/${row.user_id}`);
    }
  };

  if (isPending) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Paper>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
          padding: '12px',
          borderBottom: '1px solid #eee'
        }}
      >
        <Typography
          variant="h6"
          style={{ marginRight: 4, fontSize: 16, fontWeight: '600' }}
        >
          {title}
        </Typography>
        <span
          style={{
            background: '#F5F5F5',
            borderRadius: 8,
            padding: '4px 8px',
            fontWeight: 500,
            fontSize: 13,
            color: '#1F1F1F',
            marginLeft: 0,
            display: 'inline-block'
          }}
        >
          {totalReports}
        </span>
        {filterChips}
        <div style={{ flexGrow: 1 }} />
        {showExportButton ? <Button
          color="secondary"
          size="small"
          variant="contained"
          onClick={handleExport}
        >
          <Typography variant="button">Export</Typography>
        </Button> : null}
      </div>
      <div>
        <TableContainer className={classes.tableContainer}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.nonSortableHeaderCell}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center'
                    }}
                  >
                    Passport Status
                    <Tooltip
                      classes={{
                        popper: classes.popper,
                        tooltip: classes.tooltipContainer
                      }}
                      placement="top"
                      title={
                        <div>
                          <Typography className={classes.tooltipText}>
                            Red: User has not started any passport activity.
                          </Typography>
                          <Typography className={classes.tooltipText}>
                            Amber: User has begun passport activity but not yet
                            completed.
                          </Typography>
                          <Typography className={classes.tooltipText}>
                            Green: User has completed their passport.
                          </Typography>
                        </div>
                      }
                    >
                      <InfoOutlineIcon className={classes.infoIcon} />
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell className={classes.headerCell}>
                  <TableSortLabel
                    active={orderBy === 'user_full_name'}
                    direction={orderBy === 'user_full_name' ? order : 'asc'}
                    onClick={createSortHandler('user_full_name')}
                  >
                    Educator Name
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.headerCell}>
                  <TableSortLabel
                    active={orderBy === 'department_name'}
                    direction={orderBy === 'department_name' ? order : 'asc'}
                    onClick={createSortHandler('department_name')}
                  >
                    Department
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.headerCell}>
                  <TableSortLabel
                    active={orderBy === 'employment_type_name'}
                    direction={
                      orderBy === 'employment_type_name' ? order : 'asc'
                    }
                    onClick={createSortHandler('employment_type_name')}
                  >
                    Employment Type
                  </TableSortLabel>
                </TableCell>
                {institute?.vcm_enabled && (
                  <TableCell className={classes.headerCell}>
                    <TableSortLabel
                      active={orderBy === 'vcm_status'}
                      direction={orderBy === 'vcm_status' ? order : 'asc'}
                      onClick={createSortHandler('vcm_status')}
                    >
                      VCM Status
                    </TableSortLabel>
                  </TableCell>
                )}
                <TableCell className={classes.headerCell}>
                  <TableSortLabel
                    active={orderBy === 'profile_status'}
                    direction={orderBy === 'profile_status' ? order : 'asc'}
                    onClick={createSortHandler('profile_status')}
                  >
                    Profile Status
                  </TableSortLabel>
                </TableCell>
                {institute?.requirement_1_enabled && (
                  <TableCell className={classes.headerCell}>
                    <TableSortLabel
                      active={orderBy === 'requirement_1'}
                      direction={orderBy === 'requirement_1' ? order : 'asc'}
                      onClick={createSortHandler('requirement_1')}
                    >
                      {institute.requirement_1_name}
                    </TableSortLabel>
                  </TableCell>
                )}
                {institute?.requirement_2_enabled && (
                  <TableCell className={classes.headerCell}>
                    <TableSortLabel
                      active={orderBy === 'requirement_2'}
                      direction={orderBy === 'requirement_2' ? order : 'asc'}
                      onClick={createSortHandler('requirement_2')}
                    >
                      {institute.requirement_2_name}
                    </TableSortLabel>
                  </TableCell>
                )}
                <TableCell className={classes.headerCell}>
                  <TableSortLabel
                    active={orderBy === 'professional_points'}
                    direction={
                      orderBy === 'professional_points' ? order : 'asc'
                    }
                    onClick={createSortHandler('professional_points')}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center'
                      }}
                    >
                      Professional Points
                      <Tooltip
                        classes={{
                          popper: classes.popper,
                          tooltip: classes.tooltipContainer
                        }}
                        placement="top"
                        title={
                          <div>
                            <Typography className={classes.tooltipText}>
                              Actual/Pending/Required Professional Points.
                            </Typography>
                            <Typography className={classes.tooltipText}>
                              Actual: Points that have been completed.
                            </Typography>
                            <Typography className={classes.tooltipText}>
                              Pending: Points that have been registered for.
                            </Typography>
                            <Typography className={classes.tooltipText}>
                              Required: Points requirement for passport.
                            </Typography>
                          </div>
                        }
                      >
                        <InfoOutlineIcon className={classes.infoIcon} />
                      </Tooltip>
                    </Box>
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.headerCell}>
                  <TableSortLabel
                    active={orderBy === 'vocational_points'}
                    direction={orderBy === 'vocational_points' ? order : 'asc'}
                    onClick={createSortHandler('vocational_points')}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center'
                      }}
                    >
                      Vocational Points
                      <Tooltip
                        classes={{
                          popper: classes.popper,
                          tooltip: classes.tooltipContainer
                        }}
                        placement="top"
                        title={
                          <div>
                            <Typography className={classes.tooltipText}>
                              Actual/Pending/Required Vocational Points.
                            </Typography>
                            <Typography className={classes.tooltipText}>
                              Actual: Points that have been completed.
                            </Typography>
                            <Typography className={classes.tooltipText}>
                              Pending: Points that have been registered for.
                            </Typography>
                            <Typography className={classes.tooltipText}>
                              Required: Points requirement for passport.
                            </Typography>
                          </div>
                        }
                      >
                        <InfoOutlineIcon className={classes.infoIcon} />
                      </Tooltip>
                    </Box>
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.headerCell}>
                  <TableSortLabel
                    active={orderBy === 'learning_journeys_combined'}
                    direction={
                      orderBy === 'learning_journeys_combined' ? order : 'asc'
                    }
                    onClick={createSortHandler('learning_journeys_combined')}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center'
                      }}
                    >
                      Professional Learning Journey
                      <Tooltip
                        classes={{
                          popper: classes.popper,
                          tooltip: classes.tooltipContainer
                        }}
                        placement="top"
                        title={
                          <div>
                            <Typography className={classes.tooltipText}>
                              Actual/Pending/Required Professional Learning
                              Journeys.
                            </Typography>
                            <Typography className={classes.tooltipText}>
                              Actual: LJs that have been completed.
                            </Typography>
                            <Typography className={classes.tooltipText}>
                              Pending: LJs that have been registered for.
                            </Typography>
                            <Typography className={classes.tooltipText}>
                              Required: LJ requirement for passport.
                            </Typography>
                          </div>
                        }
                      >
                        <InfoOutlineIcon className={classes.infoIcon} />
                      </Tooltip>
                    </Box>
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.headerCell}>
                  <TableSortLabel
                    active={orderBy === 'vocational_learning_journeys_combined'}
                    direction={
                      orderBy === 'vocational_learning_journeys_combined'
                        ? order
                        : 'asc'
                    }
                    onClick={createSortHandler(
                      'vocational_learning_journeys_combined'
                    )}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center'
                      }}
                    >
                      Vocational Learning Journey
                      <Tooltip
                        classes={{
                          popper: classes.popper,
                          tooltip: classes.tooltipContainer
                        }}
                        placement="top"
                        title={
                          <div>
                            <Typography className={classes.tooltipText}>
                              Actual/Pending/Required Vocational Learning
                              Journeys.
                            </Typography>
                            <Typography className={classes.tooltipText}>
                              Actual: LJs that have been completed.
                            </Typography>
                            <Typography className={classes.tooltipText}>
                              Pending: LJs that have been registered for.
                            </Typography>
                            <Typography className={classes.tooltipText}>
                              Required: LJ requirement for passport.
                            </Typography>
                          </div>
                        }
                      >
                       <InfoOutlineIcon className={classes.infoIcon} />
                      </Tooltip>
                    </Box>
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports && reports.length > 0 ? (
                reports.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell className={classes.statusCell}>
                      <div
                        className={classes.statusDot}
                        style={{
                          backgroundColor: getPassportStatusColor(
                            row.passport_status
                          )
                        }}
                      />
                    </TableCell>
                    <TableCell
                      className={classes.clickableTableCell}
                      onClick={() => handleOnUserCellClicked(row)}
                    >
                      {row.user_full_name}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {row.department_name}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {row.employment_type_name}
                    </TableCell>
                    {institute?.vcm_enabled && (
                      <TableCell
                        className={classes.clickableTableCell}
                        onClick={() => handleOnVcmCellClicked(row)}
                      >
                        {row.vcm_status}
                      </TableCell>
                    )}
                    <TableCell
                      className={classes.clickableTableCell}
                      onClick={() => handleOnProfileCellClicked(row)}
                    >
                      {row.profile_status}
                    </TableCell>
                    {institute?.requirement_1_enabled && (
                      <TableCell className={classes.tableCell}>
                        {formatRequirementStatus(row.requirement_1)}
                      </TableCell>
                    )}
                    {institute?.requirement_2_enabled && (
                      <TableCell className={classes.tableCell}>
                        {formatRequirementStatus(row.requirement_2)}
                      </TableCell>
                    )}
                    <TableCell className={classes.tableCell}>
                      {renderProgressBar(
                        row.professional_points,
                        row.pending_professional_points,
                        row.required_professional_points
                      )}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {renderProgressBar(
                        row.vocational_points,
                        row.pending_vocational_points,
                        row.required_vocational_points
                      )}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Typography className={classes.pointsText}>
                        <span style={{ color: '#44A047', fontWeight: 600 }}>
                          {row.professional_learning_journeys}
                        </span>
                        <span style={{ color: '#000000' }}>/</span>
                        <span style={{ color: '#4A90E2', fontWeight: 600 }}>
                          {row.pending_professional_learning_journeys}
                        </span>
                        <span style={{ color: '#000000' }}>/</span>
                        <span style={{ color: '#000000', fontWeight: 600 }}>
                          {row.required_professional_learning_journeys}
                        </span>
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Typography className={classes.pointsText}>
                        <span style={{ color: '#44A047', fontWeight: 600 }}>
                          {row.vocational_learning_journeys}
                        </span>
                        <span style={{ color: '#000000' }}>/</span>
                        <span style={{ color: '#4A90E2', fontWeight: 600 }}>
                          {row.pending_vocational_learning_journeys}
                        </span>
                        <span style={{ color: '#000000' }}>/</span>
                        <span style={{ color: '#000000', fontWeight: 600 }}>
                          {row.required_vocational_learning_journeys}
                        </span>
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={
                      (institute?.vcm_enabled ? 10 : 9) +
                      (institute?.requirement_1_enabled ? 1 : 0) +
                      (institute?.requirement_2_enabled ? 1 : 0)
                    }
                    className={classes.tableCell}
                    align="center"
                  >
                    No records found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalReports}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </div>
    </Paper>
  );
};

export default withStyles(PassportTable, styles, { withTheme: true });
